import { Failure, FailureRecord, FailureFilter } from '@/types/failure';
import { FailureFilters } from '@/types/filters';
import { filterOptionsService } from '@/services/api/filterOptions';

/**
 * Transform backend failure to frontend format for analysis
 */
export function transformFailureToRecord(failure: Failure): FailureRecord {
  return {
    failure_id: failure.failure_id,
    turbine_name: failure.turbine_name,
    site_name: failure.site_name || '',
    turbine_model: failure.model_name || '',
    component_major: failure.major_component_name || '',
    component_minor: failure.component_name || '',
    failure_mode: failure.failure_mode_name || '',
    outcome_name: failure.outcome_name || '',
    failure_date: failure.down_date || failure.detected_date,
    restoration_date: failure.actual_rts_date || failure.predicted_rts_date,
    detected_date: failure.detected_date,
    commissioning_date: failure.cod_date,
    install_date: failure.install_date,
    operation_time_years: failure.operational_days / 365.25, // Convert days to years
    case_id: failure.case_id || '',
    created_at: failure.created_at,
    updated_at: failure.updated_at,
    major_corrective: failure.major_corrective ? failure.major_corrective : 'N/A',
    planned_reactive: failure.planned_reactive ? failure.planned_reactive : 'N/A',
  };
}

/**
 * Transform frontend filters (names) to backend filters (UUIDs)
 */
export async function transformFiltersToFailureFilter(
  frontendFilters: FailureFilters
): Promise<FailureFilter> {
  // Get the mappings from the service
  const mappings = await filterOptionsService.getFilterMappings();

  // Helper function to map names to UUIDs
  const mapNamesToUuids = (
    names: string[] | undefined,
    mappingKey: keyof typeof mappings
  ): string[] | undefined => {
    if (!names || names.length === 0) return undefined;

    const mapping = mappings[mappingKey];
    const mapped = names.map((name) => mapping[name]).filter((uuid) => uuid !== undefined);

    return mapped.length > 0 ? mapped : undefined;
  };

  // Build the filter object with properties only when they have values
  const filter: FailureFilter = {};

  // Only add properties if they have values
  const failureModes = frontendFilters.failureModes;
  if (failureModes && failureModes.length > 0) {
    filter.failure_mode_name = failureModes;
  }

  const siteIds = mapNamesToUuids(frontendFilters.sites, 'sites');
  if (siteIds) {
    filter.site_id = siteIds;
  }

  const componentIds = mapNamesToUuids(frontendFilters.componentsMinor, 'components');
  if (componentIds) {
    filter.component_id = componentIds;
  }

  const majorComponentIds = mapNamesToUuids(frontendFilters.componentsMajor, 'majorComponents');
  if (majorComponentIds) {
    filter.major_component_id = majorComponentIds;
  }

  const outcomeIds = mapNamesToUuids(frontendFilters.outcomeName, 'outcomeName');
  if (outcomeIds) {
    filter.outcome_id = outcomeIds;
  }

  const excludedCaseIds = frontendFilters.excludedCaseIds;
  if (excludedCaseIds && excludedCaseIds.length > 0) {
    filter.case_id = excludedCaseIds;
  }

  const modelIds = mapNamesToUuids(frontendFilters.turbineModels, 'models');
  if (modelIds) {
    filter.model_id = modelIds;
  }

  return filter;
}

/**
 * Transform frontend filters (names) to backend filters (UUIDs) synchronously
 * This version uses cached mappings and throws if not available
 */
export function transformFiltersToFailureFilterSync(
  frontendFilters: FailureFilters,
  mappings: {
    sites: Record<string, string>;
    models: Record<string, string>;
    majorComponents: Record<string, string>;
    components: Record<string, string>;
    outcomeName: Record<string, string>;
  }
): FailureFilter {
  // Helper function to map names to UUIDs
  const mapNamesToUuids = (
    names: string[] | undefined,
    mappingKey: keyof typeof mappings
  ): string[] | undefined => {
    if (!names || names.length === 0) return undefined;

    const mapping = mappings[mappingKey];
    const mapped = names.map((name) => mapping[name]).filter((uuid) => uuid !== undefined);

    return mapped.length > 0 ? mapped : undefined;
  };

  // Build the filter object with properties only when they have values
  const filter: FailureFilter = {};

  // Only add properties if they have values
  const failureModes = frontendFilters.failureModes;
  if (failureModes && failureModes.length > 0) {
    filter.failure_mode_name = failureModes;
  }

  const siteIds = mapNamesToUuids(frontendFilters.sites, 'sites');
  if (siteIds) {
    filter.site_id = siteIds;
  }

  const componentIds = mapNamesToUuids(frontendFilters.componentsMinor, 'components');
  if (componentIds) {
    filter.component_id = componentIds;
  }

  const majorComponentIds = mapNamesToUuids(frontendFilters.componentsMajor, 'majorComponents');
  if (majorComponentIds) {
    filter.major_component_id = majorComponentIds;
  }

  const outcomeIds = mapNamesToUuids(frontendFilters.outcomeName, 'outcomeName');
  if (outcomeIds) {
    filter.outcome_id = outcomeIds;
  }

  const excludedCaseIds = frontendFilters.excludedCaseIds;
  if (excludedCaseIds && excludedCaseIds.length > 0) {
    filter.case_id = excludedCaseIds;
  }

  const modelIds = mapNamesToUuids(frontendFilters.turbineModels, 'models');
  if (modelIds) {
    filter.model_id = modelIds;
  }

  return filter;
}
